import { queryOptions } from '@tanstack/react-query';

import type { Tables } from '@archetelos/db';

import { auditLog } from '@/lib/queries/admin';
// nextSortOrder + reorderSwap are reused from the catalog seam (the route
// imports them from here to keep a single symbols import); OrderSwap types the
// two-row swap a reorder produces.
import { type OrderSwap } from '@/lib/queries/catalog';
import { supabase } from '@/lib/supabase';

export type { OrderSwap };
export { nextSortOrder, reorderSwap } from '@/lib/queries/catalog';

// Named SymbolRow (not Symbol) to avoid shadowing the JS global `Symbol`.
export type SymbolRow = Tables<'symbols'>;

export interface SymbolInput {
  id?: string;
  name: string;
  slug: string;
  blurb: string;
  tradition_tags: string[];
  article_content_id: string | null;
  image_asset_id: string | null;
  is_visible: boolean;
  sort_order: number;
}

/** A published article the symbol can link to (picker option). */
export interface ArticleOption {
  id: string;
  title: string;
  slug: string;
}

/** A curated asset the symbol can use as art (picker option). */
export interface AssetOption {
  id: string;
  public_url: string;
  alt_text: string;
}

export const symbolQueries = {
  symbols: () =>
    queryOptions({
      queryKey: ['symbols'] as const,
      queryFn: async (): Promise<SymbolRow[]> => {
        const { data, error } = await supabase
          .from('symbols')
          .select('*')
          .order('sort_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as SymbolRow[];
      },
    }),

  /** Published articles for the linked-article picker. */
  publishedArticles: () =>
    queryOptions({
      queryKey: ['symbols', 'articles'] as const,
      queryFn: async (): Promise<ArticleOption[]> => {
        const { data, error } = await supabase
          .from('content')
          .select('id, title, slug')
          .eq('type', 'article')
          .eq('status', 'published')
          .order('title', { ascending: true });
        if (error) throw error;
        return (data ?? []) as ArticleOption[];
      },
    }),

  /** Curated assets for the symbol-art picker. */
  assets: () =>
    queryOptions({
      queryKey: ['symbols', 'assets'] as const,
      queryFn: async (): Promise<AssetOption[]> => {
        const { data, error } = await supabase
          .from('assets')
          .select('id, public_url, alt_text')
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data ?? []) as AssetOption[];
      },
    }),
};

export const symbolMutations = {
  saveSymbol: async (input: SymbolInput): Promise<void> => {
    const row = {
      name: input.name,
      slug: input.slug,
      blurb: input.blurb,
      tradition_tags: input.tradition_tags,
      article_content_id: input.article_content_id,
      image_asset_id: input.image_asset_id,
      is_visible: input.is_visible,
      sort_order: input.sort_order,
    };
    if (input.id) {
      const { error } = await supabase.from('symbols').update(row).eq('id', input.id);
      if (error) throw error;
      await auditLog('symbol.update', 'symbols', input.id, { name: input.name });
    } else {
      const { data, error } = await supabase.from('symbols').insert(row).select('id').single();
      if (error) throw error;
      await auditLog('symbol.create', 'symbols', data?.id ?? null, { name: input.name });
    }
  },

  // Symbols have no dependents, so delete needs no in-use guard.
  deleteSymbol: async (id: string): Promise<void> => {
    const { error } = await supabase.from('symbols').delete().eq('id', id);
    if (error) throw error;
    await auditLog('symbol.delete', 'symbols', id);
  },

  reorderSymbols: async (swap: OrderSwap): Promise<void> => {
    const a = await supabase
      .from('symbols')
      .update({ sort_order: swap.a.sort_order })
      .eq('id', swap.a.id);
    if (a.error) throw a.error;
    const b = await supabase
      .from('symbols')
      .update({ sort_order: swap.b.sort_order })
      .eq('id', swap.b.id);
    if (b.error) throw b.error;
  },
};
